import { NetworkProviderFilterType } from "@app/exports/enumerations";
import { ExportImpactProviderTypeEnum } from "@app/exports/exportModels";
import { FC, JSX, useContext, useEffect, useMemo, useRef } from "react";
import { Id, toast } from "react-toastify";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { WizardStepProps } from "@app/reports/volume-reports/import/interfaces";
import ExportService from "@app/exports/exportService";
import { VolumeReportsContext } from "@app/reports/volume-reports/import/VolumeReportsContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

export const VolumeReportsProvidersFilter: FC<React.PropsWithChildren<WizardStepProps>> = (props: WizardStepProps) => {
  const wizardContext = useContext(VolumeReportsContext);
  const userRoles = useContext(UserContext).roles;
  const exportService = new ExportService();
  const selectedProjectCountySpecialtyCount = wizardContext.projects.filter((project) => project.isChecked).reduce((previous, current) => previous + current.countySpecialtyGroupCount, 0);
  const toastIdForUploadLimit = useRef<Id | undefined>(undefined);

  useEffect(() => {
    switch (wizardContext.providerType) {
      case ProviderNetworkTypeEnum.Network:
        exportService.getNetworkServicingProviderUploadLimit().then(limit => {
          if (selectedProjectCountySpecialtyCount >= limit) {
            toastIdForUploadLimit.current = toast(getUploadLimitMessage(limit),
              {
                autoClose: false,
                containerId: "z-index-9999",
                type: "error"
              });
          }
          props.pageIsValid(selectedProjectCountySpecialtyCount < limit)
        });
        break;
      case ProviderNetworkTypeEnum.Market:
      case ProviderNetworkTypeEnum.Prospect:
        exportService.getProspectMarketServicingProviderUploadLimit().then(limit => {
          if (selectedProjectCountySpecialtyCount >= limit) {
            toastIdForUploadLimit.current = toast(getUploadLimitMessage(limit),
              {
                autoClose: false,
                containerId: "z-index-9999",
                type: "error"
              });
          }
          props.pageIsValid(selectedProjectCountySpecialtyCount < limit)
        });
        break;
      case ProviderNetworkTypeEnum.NetNew:
      case ProviderNetworkTypeEnum.NextClosestProviders:
        props.pageIsValid(true);
        break;
    }

    return () => {
      if (toastIdForUploadLimit.current && toast.isActive(toastIdForUploadLimit.current)) {
        toast.dismiss(toastIdForUploadLimit.current);
      }
    };
  }, [wizardContext.providerType]);

  const isZipProvidersReport = wizardContext.reportType === "zip-providers";
  const distinctPrefix = isZipProvidersReport ? "Distinct " : "";

  const filterOptions = useMemo(() => {
    return (
      <>
        <option key={ProviderNetworkTypeEnum.Network} value={ProviderNetworkTypeEnum.Network}>{distinctPrefix}Network Servicing Providers</option>
        <option key={ProviderNetworkTypeEnum.Prospect} value={ProviderNetworkTypeEnum.Prospect}>{distinctPrefix}Prospect Servicing Providers</option>
        <option key={ProviderNetworkTypeEnum.Market} value={ProviderNetworkTypeEnum.Market}>{distinctPrefix}Market Servicing Providers</option>
        {userRoles.includes(Roles.NET_NEW_PROVIDER_USER) && wizardContext.hasNetNewProviders && <option key={ProviderNetworkTypeEnum.NetNew} value={ProviderNetworkTypeEnum.NetNew}>{distinctPrefix}Net New Servicing Providers</option>}
        {wizardContext.isProjectWithNcpSelected && <option key={ProviderNetworkTypeEnum.NextClosestProviders} value={ProviderNetworkTypeEnum.NextClosestProviders}>Next Closest Providers</option>}
      </>)
  }, [wizardContext.isProjectWithNcpSelected, distinctPrefix])

  const radioOptions = useMemo(() => {
    // the context also resets the provider radio option to all providers in the context
    const options: JSX.Element[] = [];
    switch (wizardContext.providerType) {
      case ProviderNetworkTypeEnum.Network:
        options.push(
          <QESRadioButton
            key={NetworkProviderFilterType.Available}
            label={`All ${distinctPrefix}Providers`}
            name="provider-filter"
            value={NetworkProviderFilterType.Available}
            checked={wizardContext.networkProviderFilterType === NetworkProviderFilterType.Available}
            onChange={(e) => wizardContext.setNetworkProviderFilterType(e.target.value as NetworkProviderFilterType)}
            testId="volume-reports__network-provider-filter__all"
          />
        );
        if (userRoles.includes(Roles.TERM_PROVIDER_ADMIN) || userRoles.includes(Roles.TERM_PROVIDER_VIEWER)) {
          options.push(
            <QESRadioButton
              key={NetworkProviderFilterType.Unterminated}
              label={`${distinctPrefix}Network Minus Terminated Providers`}
              name="provider-filter"
              value={NetworkProviderFilterType.Unterminated}
              checked={wizardContext.networkProviderFilterType === NetworkProviderFilterType.Unterminated}
              onChange={(e) => wizardContext.setNetworkProviderFilterType(e.target.value as NetworkProviderFilterType)}
              testId="volume-reports__network-provider-filter__unterminated"
            />,
            <QESRadioButton
              key={NetworkProviderFilterType.Terminated}
              label={`Terminated ${distinctPrefix}Providers`}
              name="provider-filter"
              value={NetworkProviderFilterType.Terminated}
              checked={wizardContext.networkProviderFilterType === NetworkProviderFilterType.Terminated}
              onChange={(e) => wizardContext.setNetworkProviderFilterType(e.target.value as NetworkProviderFilterType)}
              testId="volume-reports__network-provider-filter__terminated"
            />
          )
        }
        break;
      case ProviderNetworkTypeEnum.Market:
      case ProviderNetworkTypeEnum.Prospect:
      case ProviderNetworkTypeEnum.NetNew:
        /**
         * The `checked` property on these is funky because TS numeric enums compile to
         * an array of <string, number> tuples, but then when it's rendered in HTML
         * the number is converted to a string. But in TS we're saving it as an enum.
         * So the compiled version is checking if (string)4 === (number)4, and it's not. 
         * Or something like that.
         * So we toString() both of them.
         * If we only toString() the (number) one TS won't compile because 
         * you "can't compare an enum with a string."
         */
        options.push(
          <QESRadioButton
            key={ExportImpactProviderTypeEnum.All}
            label={`All ${distinctPrefix}Providers`}
            name="provider-filter"
            value={ExportImpactProviderTypeEnum.All}
            checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.All.toString()}
            onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
            testId="volume-reports__impact-provider-filter__all"
          />,
          <QESRadioButton
            key={ExportImpactProviderTypeEnum.Targeted}
            label={`${distinctPrefix}Targeted Providers`}
            name="provider-filter"
            value={ExportImpactProviderTypeEnum.Targeted}
            checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.Targeted.toString()}
            onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
            testId="volume-reports__impact-provider-filter__targeted"
          />
        );

        // Add available and excluded provider options for Market and Prospect networks.
        if ([ProviderNetworkTypeEnum.Market, ProviderNetworkTypeEnum.Prospect].includes(wizardContext.providerType)) {
          options.push(
            <QESRadioButton
              key={ExportImpactProviderTypeEnum.Available}
              label={`${distinctPrefix}Available Providers`}
              name="provider-filter"
              value={ExportImpactProviderTypeEnum.Available}
              checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.Available.toString()}
              onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
              testId="volume-reports__impact-provider-filter__available"
            />,
            <QESRadioButton
              key={ExportImpactProviderTypeEnum.Excluded}
              label={`${distinctPrefix}Excluded Providers`}
              name="provider-filter"
              value={ExportImpactProviderTypeEnum.Excluded}
              checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.Excluded.toString()}
              onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
              testId="volume-reports__impact-provider-filter__excluded"
            />
          );
        }
        break;
    }
    return options;
  }, [wizardContext.providerType, wizardContext.impactProviderType, wizardContext.networkProviderFilterType, distinctPrefix])

  const getUploadLimitMessage = (limit: number): string => {
    return `Your selections exceed the ${limit} threshold, which may cause delays or incomplete results. To ensure faster processing and accuracy, please reduce your selection by adjusting your criteria.`;
  }

  return (
    <section>
      <h2 className="header-m">Provider Filter</h2>
      <p>Please select the providers to include:</p>
      <QESSelect
        label=""
        hideLabel={true}
        containerClassName="mb-2"
        testId="volume-reports__provider-type"
        value={wizardContext.providerType}
        onChange={(e) => {
          if (e.target.value !== ProviderNetworkTypeEnum.Network && wizardContext.appendPatternOfCareProviders) {
            wizardContext.setAppendPatternOfCareProviders(false);
            toast("\"Append Pattern of Care Providers\" was unchecked. Select Network Providers in the Provider Filter and MA HSD Format to Append Pattern of Care Providers.", {
              containerId: "z-index-1030",
              type: "info",
            });
          }
          wizardContext.setProviderType(e.target.value as ProviderNetworkTypeEnum);
        }}
      >
        {filterOptions}
      </QESSelect>
      {wizardContext.isProjectWithNcpSelected && wizardContext.providerType === ProviderNetworkTypeEnum.Network && (
        <QESCheckbox
          label="Append Next Closest Network Providers"
          checked={wizardContext.appendNcp}
          onChange={(e) => { wizardContext.setAppendNcp(e.target.checked); }}
          testId="volume-reports__append-ncp"
          containerClassName="mb-3"
        />
      )}
      {wizardContext.providerType.toString() !== ProviderNetworkTypeEnum.NextClosestProviders.toString() &&
        <div className="ms-3 mt-4 qes-radio-group__container">
          <p className="mb-1">Filter</p>
          {radioOptions}
        </div>
      }
    </section>
  );
}